<script setup>
import Overlay from '@/assets/js/src/components/Overlay.vue'
import { slugify, } from '@/assets/js/src/util/slugify'
import {
    DATE_SORT,
    USER_COUNT_SORT,
} from '@/assets/js/src/util/uiTools'
import ButtonListGrid from '@/assets/js/src/components/ButtonListGrid.vue'
import { stripHtml, } from '@/assets/js/src/util/composables/useStripHtml'
import { mapUserOptionByPath, } from '@/assets/js/src/modules/user/_pinia/mapUserOptionByPath'
import { getActivePinia, storeToRefs, } from 'pinia'
import { useUserStore, } from '@/assets/js/src/modules/user/_pinia/user'
import { useReadingPlanStore, } from '@/assets/js/src/modules/readingPlan/_pinia/readingPlan'
import { useReadingPlanListApi, } from '@/assets/js/src/modules/readingPlan/_composables/useReadingPlanListApi'
import { computed, getCurrentInstance, onBeforeMount, onMounted, ref, } from 'vue'
import { useHeadInfoTitle, } from '@/assets/js/src/layout/useHeadInfo'
import { useTranslation, } from '@/assets/js/src/util/composables'
import SwitchFilter from '@/assets/js/src/components/forms/SwitchFilter.vue'
import H1Title from '@/assets/js/src/components/overlay/H1Title.vue'
import SortOptions from '@/assets/js/src/components/forms/SortOptions.vue'

let activePinia = getActivePinia()
let { proxy, } = getCurrentInstance()

const { backToList, } = storeToRefs(useReadingPlanStore(activePinia))

const { loggedIn, } = storeToRefs(useUserStore(activePinia))

const {
    readingPlans,
    readingPlansMeta,
    my,
    messagesLoaded,
    allAsyncLoaded,
    asyncStatusError,
} = useReadingPlanListApi()

const gridView = computed(mapUserOptionByPath('readingPlan/gridView'))
const sort = computed(mapUserOptionByPath('readingPlan/list/sort'))
const {
    title,
} = useHeadInfoTitle({
    titleKey: 'async.reading_plan.list.title',
    descriptionKey: 'async.reading_plan.list.description',
})
const { t,tc, } = useTranslation()

const isHydrating = ref(false)

const mergedReadingPlans = computed(() => {
    let content = Object.values(readingPlans.value || {})
        .filter((item) => {
            return item.active || typeof readingPlansMeta.value[item.id]?.status !== 'undefined'
        })
        .map((item) => {
            let count = readingPlansMeta.value[item.id]?.count || 0
            let status = readingPlansMeta.value[item.id]?.status || 'none'

            return {
                ...item,
                link: `/reading-plan/${slugify(item.name)}/${item.id}`,
                count,
                status,
                interval_text: 'custom' === item.interval ? null : t(`async.reading_plan.interval.${item.interval}`),
                limited_text: t('async.reading_plan.limited'),
                count_text: tc('async.reading_plan.abo_count', count, { count, }),
                format_text: t(`async.reading_plan.format.${item.format}`),
                publisher_text: t('async.reading_plan.author', { author: (item.publisher || {}).name, }),
                section_count_text: t('async.reading_plan.section_count', { count: item.section_count, }),
                status_text: [ 'active', 'finished', ].includes(status) ? t(`async.reading_plan.status.${status}`, { item: t('async.reading_plan.item.title'), }) : '',
                intro: stripHtml(item.introduction),
            }
        })

    return isHydrating.value ? content : sortContent({ content, })
})
const sortEnum = [ USER_COUNT_SORT, DATE_SORT, ]

onBeforeMount(() => {
    isHydrating.value = true
})
onMounted(() => {
    isHydrating.value = false
    proxy.$forceUpdate()
})

const sortContent = function ({ content, }) {
    if (sort.value === DATE_SORT) {
        return content.sort((entity, nextEntity) => {
            return nextEntity.id - entity.id
        })
    } else if (sort.value === USER_COUNT_SORT) {
        return content.sort((entity, nextEntity) => {
            return nextEntity.count - entity.count
        })
    } else {
        return content
    }
}
</script>

<template>
    <overlay
        class-name="reading-plan-list"
        :full-width="true"
    >
        <v-row
            align="start"
            class="flex-wrap"
            no-gutters
        >
            <v-col
                cols="12"
                md="6"
                offset-md="3"
            >
                <template
                    v-if="allAsyncLoaded || (asyncStatusError && messagesLoaded)"
                >
                    <div class="d-flex">
                        <h1-title :messages-keys="['reading_plan']">
                            {{ title }}
                        </h1-title>

                        <v-spacer></v-spacer>

                        <button-list-grid
                            :grid-view="gridView"
                            @toggle="gridView = !gridView"
                        ></button-list-grid>
                    </div>

                    <div class="d-flex reading-plan-list__row">
                        <switch-filter
                            v-model="my"
                            label-key="async.reading_plan.list.only_my"
                            :disabled="!loggedIn"
                        />

                        <v-spacer></v-spacer>

                        <sort-options
                            v-model="sort"
                            :types="sortEnum"
                        />
                    </div>
                </template>
                <v-skeleton-loader
                    v-else
                    type="heading, text"
                    tile
                ></v-skeleton-loader>
            </v-col>
        </v-row>

        <!-- LIST/GRID-ANSICHT -->
        <transition-group
            v-if="allAsyncLoaded && mergedReadingPlans.length"
            name="scale-trans"
            tag="div"
            data-resultlist
            :class="['v-row','v-row--no-gutters',`reading-plan-list--${gridView?'grid':'list'}`]"
        >
            <v-col
                v-for="(readingPlan, index) in mergedReadingPlans"
                :key="'rp-' + readingPlan.id"
                :cols="gridView?6:12"
                :sm="gridView?4:undefined"
                :md="gridView?3:6"
                :offset-md="gridView?undefined:3"
                :lg="gridView?2:undefined"
            >
                <router-link
                    :class="[{'first': index === 0, 'elevation-1':gridView}, `reading-plan-list--${gridView?'grid':'list'}__item`]"
                    :to="{path: readingPlan.link}"
                    @click="backToList = true"
                >
                    <div
                        :class="`reading-plan-list--${gridView?'grid':'list'}__item--limiter`"
                    >
                        <div :class="`reading-plan-list--${gridView?'grid':'list'}__item--image`">
                            <bs-v-img
                                :src="((readingPlan.metadata || {}).overview || {}).src"
                                :lazy-src="((readingPlan.metadata || {}).lazy_thumb || {}).src"
                                :alt="readingPlan.name"
                                :aspect-ratio="16/9"
                                :class="['secondary--text']"
                                cover
                            >
                                <template #placeholder>
                                    <v-row
                                        class="fill-height ma-0"
                                        align="center"
                                        justify="center"
                                    >
                                        <v-progress-circular
                                            indeterminate
                                            :class="['secondary--text']"
                                        ></v-progress-circular>
                                    </v-row>
                                </template>
                            </bs-v-img>
                            <span
                                :title="readingPlan.status_text"
                            >
                                <v-icon :class="`status-${readingPlan.status}`">
                                    bs:$vuetify.icons.mdiCheckboxMarkedCircle
                                </v-icon>
                            </span>
                        </div>
                        <div :class="`reading-plan-list--${gridView?'grid':'list'}__item--content`">
                            <h2 class="text-truncate">
                                <span
                                    v-show="!gridView"
                                    :title="readingPlan.status_text"
                                    class="status-wrapper"
                                >
                                    <v-icon
                                        :class="`status-${readingPlan.status}`"
                                    >
                                        bs:$vuetify.icons.mdiCheckboxMarkedCircle
                                    </v-icon>
                                </span>
                                {{ readingPlan.name }}
                            </h2>
                            <p :class="[{'text-truncate':gridView}, `reading-plan-list--${gridView?'grid':'list'}__item--meta`]">
                                <span
                                    v-show="(readingPlan.publisher || {}).name"
                                    :title="(readingPlan.publisher || {}).name"
                                >{{ readingPlan.publisher_text }}<span
                                    v-show="!gridView"
                                >&nbsp;· </span></span><br
                                    v-show="gridView && (readingPlan.publisher || {}).name"
                                />
                                <span
                                    v-show="readingPlan.format"
                                >{{ readingPlan.format_text }}<span
                                    v-show="!gridView"
                                >&nbsp;· </span></span><br
                                    v-show="gridView && readingPlan.format"
                                />
                                <span
                                    v-show="!gridView && readingPlan.section_count && [ 'repeating', 'limited', ].includes(readingPlan.type)"
                                >{{ readingPlan.section_count_text }} · </span>
                                <span
                                    v-show="!gridView && ![ 'repeating', 'limited', ].includes(readingPlan.type)"
                                >{{ readingPlan.limited_text }} · </span>
                                <span v-show="!gridView && null !== readingPlan.interval_text">{{ readingPlan.interval_text }}</span>
                            </p>
                            <p
                                v-show="!gridView"
                                :class="`reading-plan-list--${gridView?'grid':'list'}__item--intro`"
                                v-html="readingPlan.intro"
                            ></p>
                        </div>
                    </div>
                </router-link>
            </v-col>
        </transition-group>
        <template
            v-else-if="allAsyncLoaded && !mergedReadingPlans.length"
        >
            <div :class="[`reading-plan-list--${gridView?'grid':'list'}`, 'secondary--text']">
                {{ t('async.reading_plan.list.no_items') }}
            </div>
        </template>
        <v-row
            v-else
            :class="[`reading-plan-list--${gridView?'grid':'list'}`]"
        >
            <v-col
                v-for="n in 4"
                :key="'dummy-' + n"
                :cols="gridView?6:12"
                :sm="gridView?4:undefined"
                :md="gridView?3:6"
                :offset-md="gridView?undefined:3"
                :lg="gridView?2:undefined"
            >
                <div v-show="gridView">
                    <v-skeleton-loader
                        type="card"
                        tile
                    ></v-skeleton-loader>
                </div>
                <v-row
                    v-show="!gridView"
                >
                    <v-col
                        cols="4"
                    >
                        <v-skeleton-loader
                            type="image"
                            tile
                        ></v-skeleton-loader>
                    </v-col>
                    <v-col
                        cols="8"
                    >
                        <v-skeleton-loader
                            type="heading, text@2"
                            tile
                        ></v-skeleton-loader>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </overlay>
</template>

<style lang="scss">
@use "@/assets/js/src/style/elements/sortOptions";

.bs-app {
    .reading-plan-list {
        color: var(--bs-color-black);

        @include sortOptions.sort-options;

        &__row {
            align-items: center;
            margin-bottom: map-deep-get($bs-xl, overlay, elements, marginBottom);

            .v-input--selection-controls {
                margin-top: 0;
                padding-top: 0;

                .v-label {
                    color: var(--bs-color-overlay-grey-dark);
                }
            }
        }

        &--grid, &--list {
            &__item {
                display: block;
                overflow: hidden;
                text-decoration: none;

                h2 {
                    margin: 8px 16px;
                    font-weight: 500;
                    font-size: 18px;
                }

                &:hover, &:focus {
                    h2 {
                        font-weight: 700;
                    }
                }

                &--image {
                    position: relative;

                    .v-icon {
                        position: absolute;
                        top: 8px;
                        left: 8px;
                        display: none;
                        width: 24px;
                        height: 24px;
                        background-image: radial-gradient(white 50%, transparent 50%);

                        &.status-active {
                            display: block;
                            color: map-deep-get($bs-color, bsHighlight);
                        }

                        &.status-finished {
                            display: block;
                            color: map-deep-get($bs-color, grey);
                        }
                    }
                }

                &--meta, &--intro {
                    margin: 8px 16px;

                    span {
                        color: map-deep-get($bs-color, greyDark);
                        font-size: 14px;
                    }
                }
            }
        }

        &--grid {
            margin: 0 -#{map-deep-get($bs-xl, overlay, grid, margin)};
            padding: 0 map-deep-get($bs-xl, body, paddingX);

            @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                padding: 0;
            }

            &__item {
                margin: map-deep-get($bs-xl, overlay, grid, margin);
                background-color: var(--bs-color-white);

                &--meta {
                    min-height: 54px;

                    @media (width >= 801px) and (max-width: #{map-deep-get($bs-md, breakpointValue)}px) {
                        min-height: 48px;
                    }
                }
            }
        }

        &--list {
            &__item {
                padding: 20px 16px 16px;

                &:not(.first) {
                    border-top: 1px solid var(--bs-color-grey);
                }

                h2 {
                    margin-top: 0;

                    @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                        display: flex;
                        align-items: center;
                        margin-right: 0;
                        margin-left: 0;
                    }

                    .v-icon {
                        display: none;
                        width: 24px;
                        height: 24px;
                        margin-right: 8px;

                        &.status-active {
                            color: map-deep-get($bs-color, bsHighlight);

                            @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                                display: inline-flex;
                            }
                        }

                        &.status-finished {
                            color: map-deep-get($bs-color, grey);

                            @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                                display: inline-flex;
                            }
                        }
                    }

                    .status-wrapper {
                        display: inline-flex;
                    }
                }

                &:hover, &:focus {
                    background-color: var(--bs-color-white);
                }

                &--limiter {
                    display: flex;
                }

                &--image {
                    flex-basis: 35%;
                    flex-shrink: 0;
                    max-width: 35%;

                    @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                        display: none;
                    }
                }

                &--content {
                    max-width: 65%;

                    @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                        max-width: 100%;
                    }
                }

                &--meta {
                    margin-bottom: 0;
                    line-height: 1.2em;

                    @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                        margin: 0;
                    }
                }

                &--intro {
                    display: -webkit-box;
                    max-height: calc(2 * 21px);
                    margin-top: 0;
                    overflow: hidden;
                    font-size: 14px;
                    hyphens: auto;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;

                    @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
                        margin: 0;
                    }
                }
            }
        }

        .v-skeleton-loader {
            &__text {
                height: 16px;
                margin-bottom: 24px;
            }

            &__heading {
                height: 48px;
                margin-bottom: 24px;
            }
        }

        .scale-trans {
            &-enter-from, &-leave-to {
                transform: scale(0);
                opacity: 0;
            }

            &-move {
                transition: transform .3s;
            }

            &-leave-active {
                position: absolute;
            }
        }
    }
}
</style>
